
import { useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { AnalysisResult, AnalysisProgress } from "./types";

export function useAnalysisUpdates(
  analysisId: string | undefined,
  formatResults: (result: any) => AnalysisResult,
  setResults: (results: AnalysisResult) => void,
  setProgress: React.Dispatch<React.SetStateAction<AnalysisProgress>>,
  showToast: (title: string, description: string, variant: "default" | "destructive") => void
) {
  useEffect(() => {
    if (!analysisId) return;
    
    console.log("Setting up realtime subscription for analysis ID:", analysisId);
    
    const channel = supabase
      .channel(`analysis-updates-${analysisId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'detection_results',
          filter: `id=eq.${analysisId}`
        },
        (payload) => {
          console.log("Received analysis update:", payload.new);
          const data = payload.new;
          
          if (data.metadata?.progress) {
            setProgress(prev => ({
              ...prev,
              progress: data.metadata.progress,
              status: data.metadata.status || prev.status,
              partialResults: data.metadata.partial_results 
                ? {
                    ...prev.partialResults,
                    results: data.metadata.partial_results,
                    flags: data.flags,
                    explanation: data.explanation,
                    analysisId: data.id
                  }
                : prev.partialResults
            }));
          }
          
          if (data.metadata?.status === 'completed') {
            console.log("Analysis completed, formatting results");
            const formattedResults = formatResults(data);
            setResults(formattedResults);
            setProgress({
              progress: 100,
              status: 'completed',
              partialResults: undefined
            });
            
            showToast(
              "Analysis Complete",
              `Content analysis completed with ${formattedResults.overallStatus} result.`,
              formattedResults.overallStatus === "safe" ? "default" : 
                formattedResults.overallStatus === "suspicious" ? "default" : "destructive"
            );
          }
        }
      )
      .subscribe((status) => {
        console.log("Subscription status:", status);
      });
      
    return () => {
      console.log("Cleaning up realtime subscription for:", analysisId);
      supabase.removeChannel(channel);
    };
  }, [analysisId, formatResults, setResults, setProgress, showToast]);
}
